import { z } from 'zod';
import { AuditMeta, DocId, TenantId } from '../common.js';
/**
 * Intervisiegroep — een vaste, geplande peer-supervisiegroep. De eerste
 * oplevering (#25) toonde per gebruiker de groepen waar hij lid van is met een
 * Teams-deeplink. Sinds het besluit van 6 juli beheert een suppohrt_admin de
 * groepen en hun leden IN-APP (`ledenUserIds`), omdat de Entra-route geblokkeerd
 * bleek: geen Microsoft Graph-permissies en de security-group-GUID's waren niet
 * op te vragen. In-app ledenbeheer is nu het primaire mechanisme.
 *
 * Firestore-pad: tenants/{tenantId}/intervisie_groups/{groupId}
 *
 * `entraGroupId` blijft als AANVULLEND (optioneel) koppelmechanisme bestaan voor
 * als de Graph-route later alsnog opengaat: een gebruiker ziet een groep als
 * zijn userId in `ledenUserIds` staat, ÓF zijn `entraGroupIds` (uit de
 * `groups`-claim) de `entraGroupId` van de groep bevat. Er is geen Graph-call.
 */
/**
 * Teams-deeplink. Bewust beperkt tot https teams.microsoft.com omdat de waarde
 * in de UI als `target="_blank"`-link wordt geopend — een beheerder mag hier
 * niet per ongeluk (of kwaadwillig) naar een ander origin laten wijzen.
 *
 * Geëxporteerd omdat de agenda dezelfde link accepteert (ADR 0014): elke plek
 * die een Teams-link aanneemt gebruikt deze ene validator.
 */
export const TeamsDeeplink = z
    .string()
    .url()
    .max(2048)
    .refine((raw) => {
    let parsed;
    try {
        parsed = new URL(raw);
    }
    catch {
        return false;
    }
    return (parsed.protocol === 'https:' &&
        (parsed.hostname === 'teams.microsoft.com' ||
            parsed.hostname.endsWith('.teams.microsoft.com')));
}, 'Teams-link moet een https teams.microsoft.com URL zijn');
/**
 * E-mailadres van een nog niet ingelogd lid. Lowercase genormaliseerd bij het
 * schrijven, want de login-koppeling vergelijkt letterlijk op het lowercase
 * adres uit de Entra-claim; een hoofdletter zou het lid stil laten hangen.
 */
export const PendingLidEmail = z.string().trim().toLowerCase().pipe(z.string().email().max(254));
/**
 * Voorkeursdag van een groep (besluit Jos 2 nov 2026). Een voorkeur, geen
 * regel: een losse bijeenkomst mag op een andere dag vallen. Stond tot dan als
 * vrije tekst in de omschrijving. Weekend bewust niet: intervisie is doordeweeks.
 */
export const Weekdag = z.enum(['maandag', 'dinsdag', 'woensdag', 'donderdag', 'vrijdag']);
/** Vrije tekst zoals het `time`-veld van de agenda ("19:30 - 21:00"). */
const StandaardTijd = z.string().trim().max(50);
export const IntervisieGroupSchema = AuditMeta.extend({
    id: DocId,
    tenantId: TenantId,
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    voorkeursdag: Weekdag.optional(),
    /** Vooringevulde tijd bij het plannen van bijeenkomsten. */
    standaardTijd: StandaardTijd.optional(),
    /**
     * Terugval-link: een bijeenkomst met een eigen link (één per datum, besluit
     * 2 nov 2026) gebruikt die; zonder eigen link geldt deze.
     */
    teamsUrl: TeamsDeeplink.optional(),
    /**
     * In-app geselecteerde leden (user-doc-id = entraOid voor SSO-gebruikers).
     * Primaire koppeling sinds 6 juli. Default leeg zodat legacy-docs zonder het
     * veld valide blijven en direct als "nog geen leden" gelezen worden.
     */
    ledenUserIds: z.array(z.string()).max(200).default([]),
    /**
     * Nog niet gekoppelde leden op e-mailadres, lowercase. Een groot deel van de
     * praktijkondersteuners heeft nooit ingelogd, dus bestaat er geen user-doc om
     * in `ledenUserIds` te zetten; zonder dit veld is de groepsindeling niet in te
     * voeren. Bij de eerste login verhuist het adres naar `ledenUserIds` (zelfde
     * login-time-patroon als de AI-tools-allowlist). Default leeg zodat bestaande
     * docs zonder het veld valide blijven.
     */
    pendingLedenEmails: z.array(PendingLidEmail).max(200).default([]),
    /**
     * Optioneel aanvullend koppelmechanisme: Entra security-group object-ID
     * (GUID). Was verplicht in #25; nu optioneel omdat de Graph-route geblokkeerd
     * is en ledenbeheer via `ledenUserIds` loopt.
     */
    entraGroupId: z.string().uuid().optional(),
});
/**
 * Wire-projectie voor de gebruikerspagina — geen audit-velden, geen
 * `entraGroupId` (intern koppelgegeven). `ledenUserIds` gaat wél mee: de
 * groepspagina toont de leden (via het smoelenboek, dat elk intern lid toch al
 * ziet). `teamsUrl` is optioneel: de pagina toont dan "Teams-link volgt".
 */
export const IntervisieGroupView = z.object({
    id: DocId,
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    voorkeursdag: Weekdag.optional(),
    standaardTijd: z.string().max(50).optional(),
    teamsUrl: z.string().url().optional(),
    ledenUserIds: z.array(z.string()).max(200),
});
/**
 * Beheer-request-body voor het aanmaken van een groep (suppohrt_admin).
 * `teamsUrl` en `entraGroupId` zijn optioneel; `ledenUserIds` default leeg.
 * De route valideert hiermee — nooit ongevalideerd `req.body` naar de service.
 */
export const CreateIntervisieGroupInput = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    voorkeursdag: Weekdag.optional(),
    standaardTijd: StandaardTijd.optional(),
    teamsUrl: TeamsDeeplink.optional(),
    ledenUserIds: z.array(z.string()).max(200).default([]),
    pendingLedenEmails: z.array(PendingLidEmail).max(200).default([]),
    entraGroupId: z.string().uuid().optional(),
});
/**
 * Beheer-request-body voor het bewerken van een groep. Volledige vervanging van
 * naam/omschrijving/teamsUrl/entraGroupId: een weggelaten optioneel veld wist
 * daar de bestaande waarde.
 *
 * De twee LEDENLIJSTEN wijken daar bewust van af en zijn `.optional()` in
 * plaats van `.default([])`. Met een default is "niet meegestuurd" niet te
 * onderscheiden van "leeggemaakt", en dan wist elke PUT die de lijst toevallig
 * niet meestuurt (een tweede beheerder met een oud scherm open, een herhaalde
 * request) stilzwijgend de hele groepsindeling. Weggelaten = ongewijzigd; een
 * expliciet meegestuurde lege array maakt de lijst wél leeg, zodat "alle leden
 * verwijderen" mogelijk blijft.
 */
export const UpdateIntervisieGroupInput = CreateIntervisieGroupInput.extend({
    ledenUserIds: z.array(z.string()).max(200).optional(),
    pendingLedenEmails: z.array(PendingLidEmail).max(200).optional(),
});
/**
 * Handmatig lid toevoegen (Beheer > Intervisie én Gebruikersbeheer). Bewust een
 * eigen route en geen PUT met de hele ledenlijst: twee beheerders tegelijk
 * zouden elkaars koppeling overschrijven (last-write-wins). `email` is het
 * wachtende adres dat dit lid vertegenwoordigt; meegestuurd verlaat het de
 * pending-lijst in dezelfde transactie.
 */
export const KoppelIntervisieLidInput = z.object({
    userId: z.string().min(1).max(200),
    email: PendingLidEmail.optional(),
});
/**
 * Een reeks bijeenkomsten in één keer plannen (Beheer > Intervisie). De client
 * rekent de datums uit (ritme, voorkeursdag, pauze) en laat de beheerder ze
 * aanpassen; de server krijgt alleen de definitieve lijst. Elke regel wordt een
 * los agenda-item van type `intervisie`. Max 52: een jaar wekelijks is ruim
 * meer dan een halfjaar per zes weken.
 */
export const PlanIntervisieBijeenkomstenInput = z.object({
    bijeenkomsten: z
        .array(z.object({
        date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet YYYY-MM-DD zijn'),
        time: z.string().trim().min(1).max(50),
        teamsUrl: TeamsDeeplink.optional()
            .or(z.literal(''))
            .transform((v) => v || undefined),
    }))
        .min(1)
        .max(52),
});
//# sourceMappingURL=intervisie.js.map